import React, { useState, useEffect } from "react";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import DefaultInput from "../../../../components/inputs/defaultInput";
import DefaultTextarea from "../../../../components/inputs/defaultTextarea";
import DefaultCheckbox from "../../../../components/inputs/defaultCheckbox";
import StepIndicator from "../../../../components/stepIndicator/StepIndicator";
import { HiCheckCircle, HiExclamationCircle, HiCurrencyRupee } from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";
import toast from "react-hot-toast";
import moment from "moment";
import { getSalaryStructureList, assignSalaryStructure } from "../../../../../services/workspace/salaryStructure.service";

const STEPS = [
  { id: 1, label: "Select Structure", key: "select" },
  { id: 2, label: "Effective Date", key: "date" },
  { id: 3, label: "Preview Values", key: "preview" },
  { id: 4, label: "Confirm & Save", key: "confirm" },
];

const SalaryAssignWizard = ({
  employeeId,
  employeeData,
  currentSalary,
  onClose,
  onSave,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [note, setNote] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);

  // Fetch salary structures from API
  useEffect(() => {
    fetchSalaryStructures();
  }, []);

  const fetchSalaryStructures = async () => {
    setLoadingStructures(true);
    try {
      const response = await getSalaryStructureList({ status: 1 });
      const list = response?.salaryStructure?.data || [];
      // Transform API data to match component format
      const transformed = list.map((item) => ({
        id: item.id || item.uuid,
        uuid: item.uuid,
        name: item.name,
        description: item.description || "",
        ctc: parseFloat(item.annual_ctc) || 0,
        components: (item.salaryComponent || []).map((comp) => ({
          name: comp.name,
          percentage: parseFloat(comp.value) || 0,
          taxable: comp.is_taxable === 1,
          type: comp.type || "earning",
        })),
      }));
      setSalaryStructures(transformed);
    } catch (error) {
      console.error("Failed to fetch salary structures:", error);
      
    } finally {
      setLoadingStructures(false);
    }
  };

  // Set default effective date
  useEffect(() => {
    if (!effectiveDate) {
      const today = moment().format("YYYY-MM-DD");
      setEffectiveDate(today);
    }
  }, []);

  // Calculate preview when structure and date are selected
  useEffect(() => {
    if (selectedStructure && effectiveDate && currentStep >= 3) {
      calculatePreview();
    }
  }, [selectedStructure, effectiveDate, currentStep]);

  const calculatePreview = () => {
    if (!selectedStructure) return;

    const structure = salaryStructures.find(
      (s) => s.id === selectedStructure.value || s.uuid === selectedStructure.value
    );
    if (!structure) return;

    const annualCTC = structure.ctc;
    const monthlyCTC = annualCTC / 12;

    // Separate earnings and deductions
    const earningsComponents = structure.components.filter(
      (c) => c.type === "earning" || !c.type
    );
    const deductionsComponents = structure.components.filter(
      (c) => c.type === "deduction"
    );

    const components = structure.components.map((comp) => {
      const annualAmount = (annualCTC * comp.percentage) / 100;
      const monthlyAmount = annualAmount / 12;
      return {
        ...comp,
        annualAmount,
        monthlyAmount,
      };
    });

    // Calculate totals
    const totalEarningsPercent = earningsComponents.reduce(
      (sum, c) => sum + c.percentage,
      0
    );
    const totalDeductionsPercent = deductionsComponents.reduce(
      (sum, c) => sum + c.percentage,
      0
    );

    const grossPay = (monthlyCTC * totalEarningsPercent) / 100;
    const totalDeductions = (monthlyCTC * totalDeductionsPercent) / 100;
    const netPay = grossPay - totalDeductions;

    // Only calculate oldCTC if currentSalary has valid salary_structure_id
    const hasExistingSalary = currentSalary?.salary_structure_id || currentSalary?.structureId;
    const oldCTC = hasExistingSalary ? (currentSalary?.ctc || currentSalary?.annualCTC || 0) : 0;
    const ctcDelta = hasExistingSalary ? (annualCTC - oldCTC) : 0;

    setPreviewData({
      components,
      earningsComponents: components.filter((c) => c.type === "earning" || !c.type),
      deductionsComponents: components.filter((c) => c.type === "deduction"),
      grossPay,
      totalDeductions,
      netPay,
      annualCTC,
      ctcDelta,
      oldCTC,
      totalEarningsPercent,
      totalDeductionsPercent,
    });
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedStructure) {
      toast.error("Please select a salary structure");
      return;
    }
    if (currentStep === 2) {
      // Validate effective date
      const joiningDate = employeeData?.date_of_joining;
      if (joiningDate && moment(effectiveDate).isBefore(moment(joiningDate))) {
        toast.error("Effective date cannot be before joining date");
        return;
      }
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    if (!confirmed) {
      toast.error("Please confirm before saving");
      return;
    }

    setSaving(true);

    try {
      // Get user ID
      const userId =  employeeData?.id  
      // Prepare payload for assignSalaryStructure API
      const payload = {
        salary_structure_id: selectedStructure.value,
        user_id: userId,
        effective_date: effectiveDate,
      };

      // Call assignSalaryStructure API
      await assignSalaryStructure(payload);

      const newSalary = {
        salary_structure_id: selectedStructure.value,
        structureName: selectedStructure.label,
        effectiveDate,
        note,
        ...previewData,
        status: moment(effectiveDate).isAfter(moment()) ? "upcoming" : "active",
      };

      toast.success("Salary structure assigned successfully!");
      onSave(newSalary);
    } catch (error) {
      console.error("Failed to assign salary structure:", error);
      toast.error(error?.response?.data?.message || "Failed to assign salary structure");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStructureOptions = () => {
    return salaryStructures.map((s) => ({
      value: s.id || s.uuid,
      label: `${s.name} (₹${new Intl.NumberFormat("en-IN").format(s.ctc)}/yr)`,
      structure: s,
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold text-theme mb-2">
                Select Salary Structure
              </h3>
              <p className="text-sm text-textLight">
                Choose a salary structure to assign to this employee
              </p>
            </div>

            <DefaultSearchSelectInput
              label="Salary Structure"
              required
              value={selectedStructure}
              onChange={(selected) => {
                setSelectedStructure(selected);
                setShowDetails({});
              }}
              options={getStructureOptions()}
              placeholder="Search and select structure..."
              info="Select the salary structure that will be applied to this employee"
            />

            {selectedStructure && (
              <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-theme">
                      {selectedStructure.label}
                    </h4>
                    <p className="text-xs text-textLight mt-1">
                      {selectedStructure.structure.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-textLight">Annual CTC</p>
                    <p className="text-base font-semibold text-theme">
                      {formatCurrency(selectedStructure.structure.ctc)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedStructure.structure.components.map((comp, idx) => {
                    const isDeduction = comp.type === "deduction";
                    return (
                      <span
                        key={idx}
                        className={`px-2 py-1 rounded text-xs border ${
                          isDeduction
                            ? "bg-red-50 border-red-200 text-red-700"
                            : "bg-green-50 border-green-200 text-green-700"
                        }`}
                      >
                        {isDeduction ? "📉" : "💰"} {comp.name} {comp.percentage}%
                      </span>
                    );
                  })}
                </div>

                {/* Structure Summary - Always visible */}
                {(() => {
                  const earnings = selectedStructure.structure.components.filter(
                    (c) => c.type === "earning" || !c.type
                  );
                  const deductions = selectedStructure.structure.components.filter(
                    (c) => c.type === "deduction"
                  );
                  const totalEarningsPercent = earnings.reduce((sum, c) => sum + c.percentage, 0);
                  const totalDeductionsPercent = deductions.reduce((sum, c) => sum + c.percentage, 0);
                  const netPercent = totalEarningsPercent - totalDeductionsPercent;
                  
                  const annualCtc = selectedStructure.structure.ctc;
                  const monthlyCtc = annualCtc / 12;
                  const earningsAmount = (monthlyCtc * totalEarningsPercent) / 100;
                  const deductionsAmount = (monthlyCtc * totalDeductionsPercent) / 100;
                  const netAmount = earningsAmount - deductionsAmount;

                  return (
                    <div className="p-3 bg-gray-50 rounded-lg border border-borderColor mb-3">
                      <h4 className="text-xs font-semibold text-theme mb-2">📊 Structure Summary</h4>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-[10px] text-gray-500">Earnings</p>
                          <p className="font-semibold text-green-600">{totalEarningsPercent.toFixed(1)}%</p>
                          <p className="font-bold text-green-700">{formatCurrency(earningsAmount)}/mo</p>
                        </div>
                        <div className="text-center p-2 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-[10px] text-gray-500">Deductions</p>
                          <p className="font-semibold text-red-600">{totalDeductionsPercent.toFixed(1)}%</p>
                          <p className="font-bold text-red-700">-{formatCurrency(deductionsAmount)}/mo</p>
                        </div>
                        <div className="text-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-[10px] text-gray-500">Net Salary</p>
                          <p className="font-semibold text-blue-600">{netPercent.toFixed(1)}%</p>
                          <p className="font-bold text-blue-700">{formatCurrency(netAmount)}/mo</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <button
                  type="button"
                  onClick={() =>
                    setShowDetails({
                      ...showDetails,
                      [selectedStructure.value]: !showDetails[selectedStructure.value],
                    })
                  }
                  className="text-sm text-theme hover:text-themeHover underline"
                >
                  {showDetails[selectedStructure.value]
                    ? "Hide"
                    : "View"}{" "}
                  Component Details
                </button>

                {showDetails[selectedStructure.value] && (
                  <div className="mt-3 space-y-3">
                    {/* Earnings Section */}
                    {(() => {
                      const earnings = selectedStructure.structure.components.filter(
                        (c) => c.type === "earning" || !c.type
                      );
                      if (earnings.length === 0) return null;
                      return (
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <h5 className="text-sm font-semibold text-green-700 mb-2">💰 Earnings</h5>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-green-200">
                                <th className="text-left py-2 text-green-800 font-medium">Component</th>
                                <th className="text-right py-2 text-green-800 font-medium">%</th>
                                <th className="text-right py-2 text-green-800 font-medium">Annual</th>
                                <th className="text-center py-2 text-green-800 font-medium">Taxable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {earnings.map((comp, idx) => {
                                const annualAmount = (selectedStructure.structure.ctc * comp.percentage) / 100;
                                return (
                                  <tr key={idx} className="border-b border-green-100">
                                    <td className="py-2 text-green-800">{comp.name}</td>
                                    <td className="text-right py-2 text-green-600">{comp.percentage}%</td>
                                    <td className="text-right py-2 text-green-700 font-medium">{formatCurrency(annualAmount)}</td>
                                    <td className="text-center py-2">
                                      {comp.taxable ? (
                                        <span className="text-xs text-error-600">Yes</span>
                                      ) : (
                                        <span className="text-xs text-success-600">No</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Deductions Section */}
                    {(() => {
                      const deductions = selectedStructure.structure.components.filter(
                        (c) => c.type === "deduction"
                      );
                      if (deductions.length === 0) return null;
                      return (
                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <h5 className="text-sm font-semibold text-red-700 mb-2">📉 Deductions</h5>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-red-200">
                                <th className="text-left py-2 text-red-800 font-medium">Component</th>
                                <th className="text-right py-2 text-red-800 font-medium">%</th>
                                <th className="text-right py-2 text-red-800 font-medium">Annual</th>
                                <th className="text-center py-2 text-red-800 font-medium">Taxable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deductions.map((comp, idx) => {
                                const annualAmount = (selectedStructure.structure.ctc * comp.percentage) / 100;
                                return (
                                  <tr key={idx} className="border-b border-red-100">
                                    <td className="py-2 text-red-800">{comp.name}</td>
                                    <td className="text-right py-2 text-red-600">{comp.percentage}%</td>
                                    <td className="text-right py-2 text-red-700 font-medium">-{formatCurrency(annualAmount)}</td>
                                    <td className="text-center py-2">
                                      {comp.taxable ? (
                                        <span className="text-xs text-error-600">Yes</span>
                                      ) : (
                                        <span className="text-xs text-success-600">No</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                  </div>
                )}
              </div>
            )}

            {loadingStructures && (
              <div className="bg-primary-50 border border-borderColor rounded-lg p-4 text-center">
                <CgSpinner className="animate-spin text-2xl text-theme mx-auto mb-2" />
                <p className="text-sm text-textLight">Loading salary structures...</p>
              </div>
            )}

            {!loadingStructures && salaryStructures.length === 0 && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-center">
                <p className="text-sm text-warning-900 mb-2">
                  No salary structures available
                </p>
                <DefaultButton
                  variant="outline"
                  name="Create Salary Structure"
                  onClick={() => {
                    onClose();
                    // Navigate to structure creation page
                    window.location.href = "/hr/manage-salary-structure";
                  }}
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold text-theme mb-2">
                Effective Date
              </h3>
              <p className="text-sm text-textLight">
                Select when this salary structure will become effective
              </p>
            </div>

            <DefaultDatePicker
              label="Effective Date"
              required
              value={effectiveDate}
              onChange={setEffectiveDate}
              minDate={
                employeeData?.date_of_joining
                  ? moment(employeeData.date_of_joining).toDate()
                  : null
              }
              placeholder="Select effective date"
            />

            <div className="bg-information-50 border border-information-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <HiExclamationCircle className="text-information-600 text-xl flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-information-900 mb-1">
                    Effective-Dated Record
                  </p>
                  <p className="text-xs text-information-700">
                    This will create an effective-dated record. Previous salary records will
                    remain unchanged and visible in the salary history.
                  </p>
                </div>
              </div>
            </div>

            {employeeData?.date_of_joining && (
              <div className="text-xs text-textLight">
                Employee joining date:{" "}
                {moment(employeeData.date_of_joining).format("DD MMM YYYY")}
              </div>
            )}

            <DefaultTextarea
              label="Note (Optional)"
              placeholder="Add any notes about this salary assignment..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold text-theme mb-2">
                Preview Values
              </h3>
              <p className="text-sm text-textLight">
                Review the computed salary values before saving
              </p>
            </div>

            {previewData ? (
              <>
                {/* Earnings Table */}
                {previewData.earningsComponents?.length > 0 && (
                  <div className="bg-white border border-green-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                      <h4 className="text-sm font-semibold text-green-700">💰 Earnings ({previewData.totalEarningsPercent?.toFixed(2)}%)</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-green-50/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-theme uppercase">
                              Component
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              %
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              Monthly
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              Annual
                            </th>
                            <th className="px-4 py-2 text-center text-xs font-semibold text-theme uppercase">
                              Taxable
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-borderColor">
                          {previewData.earningsComponents.map((comp, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-theme">{comp.name}</td>
                              <td className="px-4 py-2 text-sm text-textLight text-right">
                                {comp.percentage}%
                              </td>
                              <td className="px-4 py-2 text-sm text-green-700 text-right font-medium">
                                {formatCurrency(comp.monthlyAmount)}
                              </td>
                              <td className="px-4 py-2 text-sm text-green-700 text-right font-medium">
                                {formatCurrency(comp.annualAmount)}
                              </td>
                              <td className="px-4 py-2 text-center">
                                {comp.taxable ? (
                                  <span className="text-xs text-error-600">Yes</span>
                                ) : (
                                  <span className="text-xs text-success-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Deductions Table */}
                {previewData.deductionsComponents?.length > 0 && (
                  <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                      <h4 className="text-sm font-semibold text-red-700">📉 Deductions ({previewData.totalDeductionsPercent?.toFixed(2)}%)</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-red-50/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-theme uppercase">
                              Component
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              %
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              Monthly
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-semibold text-theme uppercase">
                              Annual
                            </th>
                            <th className="px-4 py-2 text-center text-xs font-semibold text-theme uppercase">
                              Taxable
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-borderColor">
                          {previewData.deductionsComponents.map((comp, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-theme">{comp.name}</td>
                              <td className="px-4 py-2 text-sm text-textLight text-right">
                                {comp.percentage}%
                              </td>
                              <td className="px-4 py-2 text-sm text-red-700 text-right font-medium">
                                -{formatCurrency(comp.monthlyAmount)}
                              </td>
                              <td className="px-4 py-2 text-sm text-red-700 text-right font-medium">
                                -{formatCurrency(comp.annualAmount)}
                              </td>
                              <td className="px-4 py-2 text-center">
                                {comp.taxable ? (
                                  <span className="text-xs text-error-600">Yes</span>
                                ) : (
                                  <span className="text-xs text-success-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-primary-50 border border-borderColor rounded-lg p-4">
                    <p className="text-xs text-textLight mb-1">Gross Pay (Monthly)</p>
                    <p className="text-xl font-semibold text-theme">
                      {formatCurrency(previewData.grossPay)}
                    </p>
                  </div>
                  <div className="bg-primary-50 border border-borderColor rounded-lg p-4">
                    <p className="text-xs text-textLight mb-1">Total Deductions</p>
                    <p className="text-xl font-semibold text-error-600">
                      {formatCurrency(previewData.totalDeductions)}
                    </p>
                  </div>
                  <div className="bg-primary-50 border border-borderColor rounded-lg p-4">
                    <p className="text-xs text-textLight mb-1">Net Pay (Monthly)</p>
                    <p className="text-xl font-semibold text-success-600">
                      {formatCurrency(previewData.netPay)}
                    </p>
                  </div>
                  <div className="bg-primary-50 border border-borderColor rounded-lg p-4">
                    <p className="text-xs text-textLight mb-1">Annual CTC</p>
                    <p className="text-xl font-semibold text-theme">
                      {formatCurrency(previewData.annualCTC)}
                    </p>
                  </div>
                  {currentSalary?.salary_structure_id && previewData.oldCTC > 0 && (
                    <div className="bg-primary-50 border border-borderColor rounded-lg p-4">
                      <p className="text-xs text-textLight mb-1">CTC Change</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-textLight">
                          {formatCurrency(previewData.oldCTC)}
                        </p>
                        <span className="text-theme">→</span>
                        <p className="text-sm font-semibold text-theme">
                          {formatCurrency(previewData.annualCTC)}
                        </p>
                      </div>
                      <p
                        className={`text-xs mt-1 ${
                          previewData.ctcDelta >= 0
                            ? "text-success-600"
                            : "text-error-600"
                        }`}
                      >
                        {previewData.ctcDelta >= 0 ? "+" : ""}
                        {formatCurrency(previewData.ctcDelta)} ({previewData.ctcDelta >= 0 ? "Increment" : "Decrement"})
                      </p>
                    </div>
                  )}
                </div>

                {/* Warnings */}
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <HiExclamationCircle className="text-warning-600 text-xl flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-warning-900 mb-1">
                        Review Required
                      </p>
                      <p className="text-xs text-warning-700">
                        Please verify all values before proceeding. Ensure bank details are
                        updated for salary processing.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-textLight">
                <CgSpinner className="animate-spin text-3xl mx-auto mb-2" />
                <p>Calculating preview...</p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-semibold text-theme mb-2">
                Confirm & Save
              </h3>
              <p className="text-sm text-textLight">
                Review the summary and confirm to save the salary assignment
              </p>
            </div>

            {/* Summary */}
            <div className="bg-primary-50 rounded-lg p-4 border border-borderColor">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-textLight">Structure:</span>
                  <span className="text-sm font-medium text-theme">
                    {selectedStructure?.label}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-textLight">Effective Date:</span>
                  <span className="text-sm font-medium text-theme">
                    {moment(effectiveDate).format("DD MMM YYYY")}
                  </span>
                </div>
                {previewData && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-textLight">Annual CTC:</span>
                      <span className="text-sm font-medium text-theme">
                        {formatCurrency(previewData.annualCTC)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-textLight">Monthly Net Pay:</span>
                      <span className="text-sm font-medium text-success-600">
                        {formatCurrency(previewData.netPay)}
                      </span>
                    </div>
                  </>
                )}
                {note && (
                  <div className="pt-2 border-t border-borderColor">
                    <span className="text-sm text-textLight">Note:</span>
                    <p className="text-sm text-theme mt-1">{note}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="bg-white border border-borderColor rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-theme border-borderColor rounded focus:ring-theme"
                />
                <span className="text-sm text-theme">
                  I confirm that the salary structure and effective date are correct. This will create an effective-dated record.
                </span>
              </label>
            </div>

            {!confirmed && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                <p className="text-xs text-error-700">
                  Please confirm before saving
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <CommonModal
      modalSize="3xl"
      headerTitle="Assign Salary Structure"
      show={true}
      onClose={onClose}
    >
      <div className="flex flex-col gap-6">
        {/* Stepper */}
        <StepIndicator steps={STEPS} currentStep={currentStep} size="compact" />

        {/* Step Content */}
        <div className="min-h-[400px]">{renderStepContent()}</div>

        {/* Actions */}
        <div className="flex justify-between border-t border-borderColor pt-4">
          <DefaultButton
            variant="secondary"
            name="Previous"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          />
          <div className="flex gap-3">
            <DefaultButton
              variant="secondary"
              name="Cancel"
              onClick={onClose}
            />
            {currentStep < STEPS.length ? (
              <DefaultButton
                variant="default"
                name="Next"
                onClick={handleNext}
                disabled={
                  (currentStep === 1 && !selectedStructure) ||
                  (currentStep === 2 && !effectiveDate)
                }
              />
            ) : (
              <DefaultButton
                variant="success"
                name="Save"
                onClick={handleSave}
                disabled={!confirmed || saving}
                loading={saving}
              />
            )}
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default SalaryAssignWizard;

